import React, { lazy, Suspense } from "react";
import { AuthProvider } from "react-oidc-context";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import { useAppSelector } from "./app/store";
import Spinner from "./components/ui/spinners/CompactLoadingSpinner";
import { onSigninCallback, userManager } from "./features/auth/logic/config";
import { Permission } from "./features/auth/logic/rbac";
import { CaseRouteId, getCaseRouteRelativePath, getWorkbenchRelativePath } from "./features/caselayout/config/caseRoutes";
import {
  getFeatureFlags,
  isBoardroomPresentationEnabled,
  isFraudElementsEnabled,
  isInternalReportsEnabled,
  isStaticDashboardDemoEnabled,
} from "./features/configs/logic/Slice";

import { PermissionGuardedRoute } from "./features/auth/components/PermissionGuardedRoute";
import { ProtectedRoute } from "./features/auth/components/ProtectedRoute";
import FeatureProtectedRoute from "./features/featureFlags/components/FeatureProtectedRoute";
import CaseLayout from "./features/caselayout/components/CaseLayout";
import GlobalLayout from "./features/pagelayout/components/GlobalSidebarLayout";
import PageLayout from "./features/pagelayout/components/PageLayout";

import CaseInfoPage from "./features/CaseInfoPage";
import AssistantPage from "./features/assistant/components/AssistantPage";
import CaseManagementPage from "./features/cases/Components/CaseManagementPage";
import CreateCasePage from "./features/create-case";
import DashboardPage from "./features/dashboard/components/DashboardPage";
import FraudElementsPage from "./features/fraudElements/components/FraudElementsPage";
import CaseRecommendationsContent from "./features/recommendations/components/CaseRecommendationsContainer";
import BoardPresentationPage from "./features/reporting/components/BoardPresentation";
import InvestigationReportPage from "./features/reporting/components/InvestigationReportPage";
import ObservationsByAuthority from "./features/reporting/components/ObservationsByAuthority/components";
import FMR1Page from "./features/reporting/components/fmr1/FMR1Page";
import LeaReportPage from "./features/reporting/components/lea/LeaPage";
import PreSCNFicPage from "./features/reporting/components/preScnFIC/PreSCNFicpage";
import RecoveryPage from "./features/reporting/components/recovery/RecoveryPage";
import { ReasonedOrderPage } from "./features/reporting/components/ro/components/ROPage";
import { ShowCauseNoticeResponsePage } from "./features/reporting/components/scn/components/SCNPage";
import { FICPageContainer } from "./features/reporting/ficDecision/components/FICPageContainer";
import SettingsPage from "./features/settings/components/SettingsPage";
import BorrowalVsNonBorrowalReportPage from "./features/TenantReports/components/borrowalVsNonBorrowal/BorrowalVsNonBorrowalReportPage";
import FraudByFicCategoryReportPage from "./features/TenantReports/components/fraudByFicCategory/FraudByFicCategoryReportPage";
import InternalReportsLandingPage from "./features/TenantReports/components/reportsLanding/InternalReportsLandingPage";
import UserProfilePage from "./features/userprofile/components/UserProfile";
import WorkbenchLayout from "./features/workbench/components/WorkbenchLayout";
import AccountDetailsPage from "./features/workbench/components/AccountDetailsPage";
import { AnalysisPage } from "./features/workbench/components/AnalysisPage";
import ObservationsRecommendationsPage from "./features/workbench/components/ObservationsRecommendationsPage";
import InvestigationDocumentsPage from "./features/documentManagement/components/InvestigationDocumentsPage";
import { isAccountDetailsNavEnabled, isInspectionsAndAuditsNavEnabled } from "./features/caseinfo/logic/Slice";
import InspectionsAuditsPage from "./features/workbench/components/InspectionsAuditsPage";


const StaticDashboardDemo = lazy(
  () => import("./components/demo/dashboard/dashboard_aug_2026")
);

const DashboardRoute = () => {
  const flags = useAppSelector(getFeatureFlags);
  const showStaticDashboardDemo = useAppSelector(isStaticDashboardDemoEnabled);

  if (Object.keys(flags).length === 0) {
    return <Spinner isFixed message="Loading dashboard..." showDefaultSubtitle={false} />;
  }

  if (!showStaticDashboardDemo) {
    return <DashboardPage />;
  }

  return (
    <Suspense fallback={<Spinner isFixed message="Loading dashboard..." showDefaultSubtitle={false} />}>
      <StaticDashboardDemo />
    </Suspense>
  );
};

export default function App() {
  return (
    <AppProviders>
      <div className="flex h-screen bg-background text-foreground">
        <AppRouter />
      </div>
    </AppProviders>
  );
}

function AppProviders({ children }: { children: React.ReactNode }) {
  return (
    <>
      <ToastContainer
        position="bottom-right"
        autoClose={4000}
        closeButton={false}
        closeOnClick
        pauseOnFocusLoss
        pauseOnHover
        theme="colored"
      />
      <BrowserRouter>
        <AuthProvider userManager={userManager} onSigninCallback={onSigninCallback}>
          {children}
        </AuthProvider>
      </BrowserRouter>
    </>
  );
}

function AppRouter() {
  return (
    <Routes>
      <Route element={<ProtectedRoute />}>
        <Route element={<PageLayout />}>
          <Route element={<GlobalLayout />}>
            <Route path="/cases" element={<PermissionGuardedRoute permission={Permission.LIST_CASES} />}>
              <Route index element={<CaseManagementPage />} />
            </Route>

            <Route path="/cases/create" element={<CreateCasePage />} />

            <Route path="/dashboard" element={<PermissionGuardedRoute permission={Permission.ACCESS_DASHBOARD} />}>
              <Route index element={<DashboardRoute />} />
            </Route>

            <Route path="/assistant" element={<AssistantPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/user-profile" element={<UserProfilePage />} />

            <Route path="/internal-reports" element={<PermissionGuardedRoute permission={Permission.ACCESS_INTERNAL_REPORTS} />}>
              <Route element={<FeatureProtectedRoute selector={isInternalReportsEnabled} fallbackPath="/cases" />}>
                <Route index element={<InternalReportsLandingPage />} />
                <Route path="borrowal-vs-non-borrowal" element={<BorrowalVsNonBorrowalReportPage />} />
                <Route path="fraud-by-fic-category" element={<FraudByFicCategoryReportPage />} />
              </Route>
            </Route>
          </Route>

          <Route path="/cases/:id" element={<PermissionGuardedRoute permission={Permission.OPEN_CASE} />}>
            <Route element={<CaseLayout />}>
              <Route index element={<CaseInfoPage />} />

              <Route path={getCaseRouteRelativePath(CaseRouteId.WORKBENCH)} element={<PermissionGuardedRoute permission={Permission.ACCESS_INVESTIGATION} />}>
                <Route  element={<WorkbenchLayout />} >
                  <Route path={getWorkbenchRelativePath(CaseRouteId.ACCOUNT_DETAILS)} element={<FeatureProtectedRoute selector={isAccountDetailsNavEnabled} fallbackPath="/cases" />}>
                    <Route index element={<AccountDetailsPage />} />
                  </Route>
                  <Route path={getWorkbenchRelativePath(CaseRouteId.ANALYSIS)} element = {<AnalysisPage />}/>
                  <Route path={getWorkbenchRelativePath(CaseRouteId.INSPECTIONS_AUDITS)} element={<FeatureProtectedRoute selector={isInspectionsAndAuditsNavEnabled} fallbackPath="/cases" />}>
                    <Route index element={<InspectionsAuditsPage />} />
                  </Route>
                  <Route path={getWorkbenchRelativePath(CaseRouteId.OBSERVATIONS_RECOMMENDATIONS)} element={<ObservationsRecommendationsPage />}/>
                  <Route path={getWorkbenchRelativePath(CaseRouteId.INVESTIGATION_DOCUMENTS)} element={<InvestigationDocumentsPage />}/>
                </Route>
              </Route>

              <Route element={<PermissionGuardedRoute permission={Permission.ACCESS_INVESTIGATION_REPORT} />}>
                <Route path={getCaseRouteRelativePath(CaseRouteId.IR)} element={<InvestigationReportPage />} />
              </Route>

              <Route element={<PermissionGuardedRoute permission={Permission.ACCESS_RECOMMENDATIONS} />}>
                <Route path={getCaseRouteRelativePath(CaseRouteId.RECOMMENDATIONS)} element={<CaseRecommendationsContent />} />
              </Route>

              <Route element={<PermissionGuardedRoute permission={Permission.ACCESS_CASE_REPORTS} />}>
                <Route path={getCaseRouteRelativePath(CaseRouteId.REPORT_PRE_SCN_FIC)} element={<PreSCNFicPage />} />
                <Route path={getCaseRouteRelativePath(CaseRouteId.REPORT_SCN)} element={<ShowCauseNoticeResponsePage />} />
                <Route path={getCaseRouteRelativePath(CaseRouteId.REPORT_FIC)} element={<FICPageContainer />} />
                <Route path={getCaseRouteRelativePath(CaseRouteId.REPORT_FMR)} element={<FMR1Page />} />
                <Route path={getCaseRouteRelativePath(CaseRouteId.REPORT_RO)} element={<ReasonedOrderPage />} />
                <Route path={getCaseRouteRelativePath(CaseRouteId.REPORT_LEA)} element={<LeaReportPage />} />
                <Route path={getCaseRouteRelativePath(CaseRouteId.REPORT_RECOVERY)} element={<RecoveryPage />} />

                <Route path={getCaseRouteRelativePath(CaseRouteId.FRAUD_ELEMENTS)} element={<FeatureProtectedRoute selector={isFraudElementsEnabled} fallbackPath="/cases" />}>
                  <Route index element={<FraudElementsPage />} />
                </Route>
              </Route>

              <Route path="reports">
                <Route element={<PermissionGuardedRoute permission={Permission.ACCESS_CASE_REPORTS} />}>
                  <Route path="board-ppt" element={<FeatureProtectedRoute selector={isBoardroomPresentationEnabled} fallbackPath="/cases" />}>
                    <Route index element={<BoardPresentationPage />} />
                  </Route>
                </Route>
              </Route>

              <Route element={<PermissionGuardedRoute permission={Permission.OBSERVATIONS_BY_AUTHORITY} />}>
                <Route path={getCaseRouteRelativePath(CaseRouteId.OBSERVATIONS_BY_AUTHORITY)} element={<ObservationsByAuthority />} />
              </Route>
            </Route>
          </Route>

          <Route path="/*" element={<Navigate to="/cases" replace />} />
        </Route>
      </Route>
    </Routes>
  );
}